import { priceMenu, type MenuItem } from "./services";

/**
 * Single source of truth for all customer-facing prices.
 *
 * Never write a price as a literal in a page component. Read it from here so
 * that a change in `priceMenu` (src/data/services.ts) propagates everywhere.
 */

export const menuItems: MenuItem[] = priceMenu.flatMap((c) => c.items);

const byName = new Map<string, MenuItem>(menuItems.map((i) => [i.name.toLowerCase(), i]));

/** Canonical menu item by exact name. Throws in dev if the name does not exist. */
export function menuItem(name: string): MenuItem {
  const item = byName.get(name.toLowerCase());
  if (!item) {
    throw new Error(
      `Unknown price menu item "${name}". Prices must exist in priceMenu (src/data/services.ts).`,
    );
  }
  return item;
}

/** Numeric price in SEK, or undefined when the price varies. */
export function priceKr(name: string): number | undefined {
  return menuItem(name).priceKr;
}

/** "450 kr" — or "Varierat pris" when the price varies. */
export function priceLabel(name: string): string {
  const kr = priceKr(name);
  return kr === undefined ? "Varierat pris" : `${kr} kr`;
}

/** "Från 450 kr" — or "Varierat pris". */
export function fromPriceLabel(name: string): string {
  const kr = priceKr(name);
  return kr === undefined ? "Varierat pris" : `Från ${kr} kr`;
}

/** Convenience constants for the prices referenced most often in copy. */
export const PRICE = {
  herrklippning: priceKr("Herrklippning")!,
  fade: priceKr("Skin Fade / Taper Fade")!,
  skaggtrimning: priceKr("Skäggtrimning")!,
  klippningSkagg: priceKr("Klippning & Skäggtrimning")!,
  barnklippning: priceKr("Barnklippning")!,
  seniorklippning: priceKr("Seniorklippning 65+")!,
  slatrakning: priceKr("Slätrakning")!,
} as const;

/**
 * Short villkor that MUST follow the service name wherever the price is shown.
 * Without these a customer can arrive expecting a price that does not apply.
 */
const QUALIFIER: Record<string, string> = {
  "seniorklippning 65+": "mån–tors 10–14",
  barnklippning: "upp till 10 år",
};

/** Display name including its villkor, e.g. "Seniorklippning 65+ (mån–tors 10–14)". */
export function priceName(name: string): string {
  const q = QUALIFIER[name.toLowerCase()];
  return q ? `${name} (${q})` : name;
}

/** Rows for a simple two-column price table. */
export function priceRows(names: string[]): string[][] {
  return names.map((n) => [priceName(n), priceLabel(n)]);
}
